import { sitesApi } from "#/api/sitesApi";
import { subClientsApi } from "#/api/subClientsApi";
import { uploadApi } from "#/api/uploadApi";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import EntityAvatar from "#/components/ui/EntityAvatar";
import ImagePicker from "#/components/ui/ImagePicker";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import { useUnsavedChangesWarning } from "#/hooks/useUnsavedChangesWarning";
import {
  AlertCondition,
  AlertNotificationMode,
  CompanyType,
  type CreateSubClientPayload,
  EmployeeCountRange,
  INDUSTRY_OPTIONS,
  type ProductRotation,
  RevenueRange,
  type SubClient,
  type SubClientCategoryAlert,
} from "#/types/subClient";
import { logger } from "#/utils/logger";
import { Accordion, Button, NumberInput, Select, Tabs, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AlertsAccordion from "./components/AlertsAccordion";
import AnalyticsTab from "./components/AnalyticsTab";
import SubClientPatternsTab from "./components/SubClientPatternsTab";
import CustomFieldsAccordion from "./components/CustomFieldsAccordion";
import RotationAccordion from "./components/RotationAccordion";

type LocationMode = "address" | "coordinates";

interface FormShape extends CreateSubClientPayload {
  alerts: SubClientCategoryAlert[];
  rotations: ProductRotation[];
  customFields: Record<string, unknown>;
  latitude?: number;
  longitude?: number;
}

/** The editable part of a sub-client, as the form holds it. The logo is kept outside the form. */
const toFormValues = (sub: SubClient): FormShape => ({
  name: sub.name ?? "",
  address: sub.address ?? "",
  latitude: sub.latitude != null ? Number(sub.latitude) : undefined,
  longitude: sub.longitude != null ? Number(sub.longitude) : undefined,
  contactPersonName: sub.contactPersonName ?? "",
  contactPersonEmail: sub.contactPersonEmail ?? "",
  contactPersonPhone: sub.contactPersonPhone ?? "",
  industry: sub.industry ?? null,
  siteId: sub.site?.id ?? undefined,
  employeeCount: sub.employeeCount ?? undefined,
  annualRevenue: sub.annualRevenue ?? undefined,
  companyType: sub.companyType ?? undefined,
  customFields: (sub.customFields as Record<string, unknown>) ?? {},
  alerts: (sub.alerts ?? []).map((a) => ({
    id: a.id,
    productId: a.product?.id ?? 0,
    productName: a.product?.name,
    condition: a.condition,
    threshold: a.threshold,
    notificationMode: a.notificationMode,
  })),
  rotations: (sub.rotations ?? []).map((r) => ({
    id: r.id,
    productId: r.product?.id ?? 0,
    productName: r.product?.name,
    days: r.days,
    count: r.count,
  })),
});

const SubClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = id === "new";

  const [activeTab, setActiveTab] = useState<string>(
    searchParams.get("tab") || "details",
  );
  const [locationMode, setLocationMode] = useState<LocationMode>("address");

  // Logo lives outside the form: `logoUrl` is what is stored, `logoFile` a new
  // pick awaiting upload, `logoDirty` whether a pick or clear is unsaved.
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoDirty, setLogoDirty] = useState(false);

  const form = useForm<FormShape>({
    initialValues: {
      name: "",
      address: "",
      latitude: undefined,
      longitude: undefined,
      contactPersonName: "",
      contactPersonEmail: "",
      contactPersonPhone: "",
      industry: null,
      siteId: undefined,
      employeeCount: undefined,
      annualRevenue: undefined,
      companyType: undefined,
      customFields: {},
      alerts: [],
      rotations: [],
    },
    validate: {
      name: (value) => (value ? null : t("subClients.validation.nameRequired")),
      address: (value) => {
        if (locationMode !== "address") return null;
        return value ? null : t("subClients.validation.addressRequired");
      },
      latitude: (value) => {
        if (locationMode !== "coordinates") return null;
        if (typeof value !== "number")
          return t("subClients.validation.latitudeRequired");
        if (value < -90 || value > 90)
          return t("subClients.validation.latitudeRange");
        return null;
      },
      longitude: (value) => {
        if (locationMode !== "coordinates") return null;
        if (typeof value !== "number")
          return t("subClients.validation.longitudeRequired");
        if (value < -180 || value > 180)
          return t("subClients.validation.longitudeRange");
        return null;
      },
      contactPersonEmail: (value) => {
        if (!value) return null;
        return /^\S+@\S+$/.test(value)
          ? null
          : t("subClients.validation.invalidEmail");
      },
      alerts: {
        productId: (value) =>
          value && value > 0 ? null : t("subClients.validation.productRequired"),
        threshold: (value) =>
          value !== undefined && value !== null && value >= 0
            ? null
            : t("subClients.validation.thresholdRequired"),
      },
      rotations: {
        productId: (value) =>
          value && value > 0 ? null : t("subClients.validation.productRequired"),
        days: (value) =>
          value !== undefined && value !== null && value >= 0
            ? null
            : t("subClients.validation.daysRequired"),
        count: (value) =>
          value !== undefined && value !== null && value >= 0
            ? null
            : t("subClients.validation.countRequired"),
      },
    },
  });

  const { data: { sites = [] } = {} } = useQuery({
    queryKey: ["sites-all"],
    queryFn: async () => {
      const response = await sitesApi.getAll({ page: 1, limit: 10000 });
      return { sites: response.data.data };
    },
    staleTime: 1000 * 60 * 5,
  });

  const { data: subClient } = useQuery({
    queryKey: ["sub-client", id],
    enabled: !!id && id !== "new",
    queryFn: async () => {
      const res = await subClientsApi.getById(Number(id));
      return res.data;
    },
    retry: false,
  });

  // Load the fetched sub-client into the form, but never over unsaved edits:
  // React Query refetches on window focus, and that would wipe what the user
  // is typing. Each fetched object is applied once; while the form (or the
  // logo) is dirty it is left alone and picked up after the next save makes
  // the page clean again.
  const appliedSubClientRef = useRef<SubClient | null>(null);
  useEffect(() => {
    if (
      !subClient ||
      appliedSubClientRef.current === subClient ||
      form.isDirty() ||
      logoDirty
    )
      return;
    const values = toFormValues(subClient);
    form.setInitialValues(values);
    form.setValues(values);
    form.resetDirty(values);
    setLocationMode("address");
    setLogoUrl(subClient.logo ?? null);
    appliedSubClientRef.current = subClient;
  }, [subClient, form, logoDirty]);

  useUnsavedChangesWarning(form.isDirty() || logoDirty);

  const resetLogo = () => {
    setLogoFile(null);
    setLogoUrl(subClient?.logo ?? null);
    setLogoDirty(false);
  };

  const handleLogoChange = (file: File | null) => {
    setLogoFile(file);
    // The picker's remove button clears the stored image too.
    if (!file) setLogoUrl(null);
    setLogoDirty(true);
  };

  const mutation = useMutation({
    mutationFn: async (values: FormShape) => {
      const isCoordsMode = locationMode === "coordinates";
      const payload: CreateSubClientPayload = {
        name: values.name,
        address: isCoordsMode ? undefined : values.address,
        latitude: isCoordsMode ? values.latitude : undefined,
        longitude: isCoordsMode ? values.longitude : undefined,
        // `null` (not `undefined`) so an explicit clear actually persists —
        // `undefined` keys get dropped by JSON and never reach the backend.
        contactPersonName: values.contactPersonName || null,
        contactPersonEmail: values.contactPersonEmail || null,
        contactPersonPhone: values.contactPersonPhone || null,
        industry: values.industry ?? null,
        siteId: values.siteId ?? null,
        employeeCount: values.employeeCount ?? null,
        annualRevenue: values.annualRevenue ?? null,
        companyType: values.companyType ?? null,
        customFields:
          values.customFields && Object.keys(values.customFields).length > 0
            ? values.customFields
            : null,
        alerts: values.alerts.map((a) => ({
          ...(a.id ? { id: a.id } : {}),
          productId: a.productId,
          condition: a.condition,
          threshold: a.threshold,
          notificationMode: a.notificationMode ?? AlertNotificationMode.ON_STATE_CHANGE,
        })),
        rotations: values.rotations.map((r) => ({
          ...(r.id ? { id: r.id } : {}),
          productId: r.productId,
          days: r.days,
          count: r.count,
        })),
      };

      // New pick: upload first and store its URL. Cleared: `null` removes the
      // stored logo. Untouched: leave the key out so the backend keeps it.
      if (logoFile) {
        payload.logo = await uploadApi.upload(logoFile);
      } else if (logoDirty && logoUrl === null) {
        payload.logo = null;
      }

      if (isNew) {
        return subClientsApi.create(payload);
      }
      return subClientsApi.update(Number(id), payload);
    },
    onSuccess: (data) => {
      showSuccessNotification({
        message: isNew
          ? t("subClients.createdSuccess")
          : t("subClients.updatedSuccess"),
      });
      if (isNew) {
        void navigate(`/sub-clients/${data.data.id}`);
      } else {
        // The form now matches what is stored: make that its clean baseline so
        // Save disables and the refetch has nothing to overwrite.
        const saved = toFormValues(data.data);
        form.setInitialValues(saved);
        form.setValues(saved);
        form.resetDirty(saved);
        setLogoFile(null);
        setLogoUrl(data.data.logo ?? null);
        setLogoDirty(false);
        void queryClient.invalidateQueries({ queryKey: ["sub-client", id] });
      }
    },
    onError: (error) => {
      logger.error("SubClientDetail save error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const handleSubmit = (values: FormShape) => {
    if (
      values.alerts.some(
        (a) =>
          !a.productId ||
          a.productId <= 0 ||
          a.threshold == null ||
          a.threshold < 0 ||
          !Object.values(AlertCondition).includes(a.condition),
      )
    ) {
      showErrorNotification({
        message: t("subClients.validation.alertsInvalid"),
      });
      return;
    }
    if (
      values.rotations.some(
        (r) =>
          !r.productId ||
          r.productId <= 0 ||
          r.days == null ||
          r.days < 0 ||
          r.count == null ||
          r.count < 0,
      )
    ) {
      showErrorNotification({
        message: t("subClients.validation.rotationsInvalid"),
      });
      return;
    }
    mutation.mutate(values);
  };

  const isDirty = form.isDirty() || logoDirty;

  return (
    <DetailPageLayout
      title={
        isNew ? (
          t("subClients.createNew")
        ) : (
          <div className="flex items-center gap-3">
            <EntityAvatar src={logoUrl} name={form.values.name} size={40} />
            <span>{t("subClients.detail")}</span>
          </div>
        )
      }
      navRightSection={
        activeTab === "details" ? (
          <div className="w-full flex justify-end gap-2 items-center">
            <Button
              color="black"
              variant="outline"
              type="reset"
              disabled={!isDirty}
              onClick={() => {
                form.reset();
                resetLogo();
              }}
              size="xs"
            >
              {t("common.clear")}
            </Button>
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                color="black"
                variant="filled"
                loading={mutation.isPending}
                disabled={!isDirty || !canWrite()}
                onClick={() => form.onSubmit(handleSubmit)()}
                size="xs"
              >
                {isNew ? t("common.create") : t("common.save")}
              </Button>
            </PermissionTooltip>
          </div>
        ) : null
      }
    >
      <div className="h-full flex flex-col min-h-0">
        <Tabs
          value={activeTab}
          onChange={(value) => {
            const next = value || "details";
            setActiveTab(next);
            setSearchParams({ tab: next });
          }}
          className="w-full h-full flex flex-col"
        >
          <Tabs.List className="flex-shrink-0">
            <Tabs.Tab value="details">{t("subClients.tabs.details")}</Tabs.Tab>
            {!isNew && (
              <Tabs.Tab value="analytics">
                {t("subClients.tabs.analytics")}
              </Tabs.Tab>
            )}
            {!isNew && (
              <Tabs.Tab value="patterns">{t("subClients.tabs.patterns")}</Tabs.Tab>
            )}
          </Tabs.List>

          <Tabs.Panel value="details" pt="md" className="flex-1 overflow-auto min-h-0">
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Accordion
                variant="separated"
                defaultValue={["basic", "contact", "location", "alerts", "rotations", "custom_fields"]}
                multiple
              >
                <Accordion.Item value="basic">
                  <Accordion.Control>
                    {t("common.basicDetails")}
                  </Accordion.Control>
                  <Accordion.Panel>
                    <div className="w-full flex flex-col gap-4">
                      <div className="w-full flex flex-col gap-1">
                        <ImagePicker
                          variant="inline"
                          label={t("subClients.form.logo")}
                          value={logoUrl}
                          file={logoFile}
                          onChange={handleLogoChange}
                          alt={form.values.name || t("subClients.form.logo")}
                          clearable
                          disabled={!canWrite()}
                        />
                        <p className="text-xs text-gray-500">{t("subClients.form.logoHint")}</p>
                      </div>
                      <div className="w-full grid grid-cols-2 gap-4">
                        <TextInput
                          label={t("subClients.form.name")}
                          withAsterisk
                          placeholder={t("subClients.form.namePlaceholder")}
                          disabled={!canWrite()}
                          {...form.getInputProps("name")}
                        />
                        <Select
                          label={t("subClients.form.site")}
                          placeholder={t("subClients.form.sitePlaceholder")}
                          searchable
                          clearable
                          disabled={!canWrite()}
                          data={sites.map((site) => ({
                            label: site.name,
                            value: site.id.toString(),
                          }))}
                          value={form.values.siteId ? form.values.siteId.toString() : null}
                          onChange={(value) =>
                            form.setFieldValue("siteId", value ? Number(value) : undefined)
                          }
                        />
                        <Select
                          label={t("subClients.form.industry")}
                          placeholder={t("subClients.form.selectPlaceholder")}
                          clearable
                          searchable
                          disabled={!canWrite()}
                          data={INDUSTRY_OPTIONS}
                          {...form.getInputProps("industry")}
                        />
                        <Select
                          label={t("subClients.form.employeeCount")}
                          placeholder={t("subClients.form.selectPlaceholder")}
                          clearable
                          disabled={!canWrite()}
                          data={Object.values(EmployeeCountRange)}
                          {...form.getInputProps("employeeCount")}
                        />
                        <Select
                          label={t("subClients.form.annualRevenue")}
                          placeholder={t("subClients.form.selectPlaceholder")}
                          clearable
                          disabled={!canWrite()}
                          data={Object.values(RevenueRange)}
                          {...form.getInputProps("annualRevenue")}
                        />
                        <Select
                          label={t("subClients.form.companyType")}
                          placeholder={t("subClients.form.selectPlaceholder")}
                          clearable
                          disabled={!canWrite()}
                          data={[
                            { value: CompanyType.PRIVATE, label: t("subClients.form.private") },
                            { value: CompanyType.PUBLIC, label: t("subClients.form.public") },
                          ]}
                          {...form.getInputProps("companyType")}
                        />
                      </div>
                    </div>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="contact">
                  <Accordion.Control>
                    {t("subClients.sections.contact")}
                  </Accordion.Control>
                  <Accordion.Panel>
                    <div className="w-full grid grid-cols-3 gap-4">
                      <TextInput
                        label={t("subClients.form.contactPersonName")}
                        placeholder={t(
                          "subClients.form.contactPersonNamePlaceholder",
                        )}
                        disabled={!canWrite()}
                        {...form.getInputProps("contactPersonName")}
                      />
                      <TextInput
                        label={t("subClients.form.contactPersonEmail")}
                        placeholder={t(
                          "subClients.form.contactPersonEmailPlaceholder",
                        )}
                        disabled={!canWrite()}
                        {...form.getInputProps("contactPersonEmail")}
                      />
                      <TextInput
                        label={t("subClients.form.contactPersonPhone")}
                        placeholder={t(
                          "subClients.form.contactPersonPhonePlaceholder",
                        )}
                        disabled={!canWrite()}
                        {...form.getInputProps("contactPersonPhone")}
                      />
                    </div>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="location">
                  <Accordion.Control>
                    <div className="flex items-center gap-3 w-full pr-4">
                      <span>{t("subClients.sections.location")}</span>
                      <Button
                        variant="subtle"
                        color="blue"
                        size="xs"
                        disabled={!canWrite()}
                        onClick={(e) => {
                          e.stopPropagation();
                          setLocationMode(
                            locationMode === "address" ? "coordinates" : "address",
                          );
                        }}
                      >
                        {locationMode === "address"
                          ? t("subClients.form.useCoordinatesInstead")
                          : t("subClients.form.useAddressInstead")}
                      </Button>
                    </div>
                  </Accordion.Control>
                  <Accordion.Panel>
                    {locationMode === "address" ? (
                      <TextInput
                        label={t("subClients.form.address")}
                        withAsterisk
                        placeholder={t("subClients.form.addressPlaceholder")}
                        disabled={!canWrite()}
                        {...form.getInputProps("address")}
                      />
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <NumberInput
                          label={t("subClients.form.latitude")}
                          withAsterisk
                          placeholder={t("subClients.form.latitudePlaceholder")}
                          decimalScale={8}
                          min={-90}
                          max={90}
                          disabled={!canWrite()}
                          value={form.values.latitude ?? ""}
                          onChange={(value) =>
                            form.setFieldValue(
                              "latitude",
                              typeof value === "number" ? value : undefined,
                            )
                          }
                          error={form.errors.latitude}
                        />
                        <NumberInput
                          label={t("subClients.form.longitude")}
                          withAsterisk
                          placeholder={t("subClients.form.longitudePlaceholder")}
                          decimalScale={8}
                          min={-180}
                          max={180}
                          disabled={!canWrite()}
                          value={form.values.longitude ?? ""}
                          onChange={(value) =>
                            form.setFieldValue(
                              "longitude",
                              typeof value === "number" ? value : undefined,
                            )
                          }
                          error={form.errors.longitude}
                        />
                      </div>
                    )}
                  </Accordion.Panel>
                </Accordion.Item>

                <AlertsAccordion
                  alerts={form.values.alerts}
                  onChange={(alerts) => form.setFieldValue("alerts", alerts)}
                />

                <RotationAccordion
                  rotations={form.values.rotations}
                  onChange={(rotations) => form.setFieldValue("rotations", rotations)}
                />

                <CustomFieldsAccordion
                  fields={form.values.customFields}
                  onChange={(fields) =>
                    form.setFieldValue("customFields", fields)
                  }
                />
              </Accordion>
            </form>
          </Tabs.Panel>

          {!isNew && (
            <Tabs.Panel value="analytics" pt="md" className="flex-1 overflow-auto min-h-0">
              <AnalyticsTab subClientId={Number(id)} />
            </Tabs.Panel>
          )}

          {!isNew && (
            <Tabs.Panel value="patterns" pt="md" className="flex-1 overflow-auto min-h-0">
              <SubClientPatternsTab subClientId={Number(id)} />
            </Tabs.Panel>
          )}
        </Tabs>
      </div>
    </DetailPageLayout>
  );
};

export default SubClientDetailPage;
